import { StyleSheet, Text, type TextProps } from "react-native";

import { FontSize } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";

type Variant =
  | "display"
  | "title"
  | "subtitle"
  | "body"
  | "bodyStrong"
  | "label"
  | "caption"
  | "link";

type Tone =
  | "default"
  | "secondary"
  | "muted"
  | "tint"
  | "onTint"
  | "danger"
  | "success";

export type ThemedTextProps = TextProps & {
  variant?: Variant;
  tone?: Tone;
  center?: boolean;
};

/**
 * There is deliberately no per-variant cap table here any more.
 *
 * There used to be one — display/title 1.4, body 1.6, label/caption 1.4 — and
 * it was the reason scaling looked uneven on device. An explicit
 * `maxFontSizeMultiplier` beats the app-wide ceiling patched into React
 * Native's `Text` (see patches/react-native+0.85.3.patch), so every ThemedText
 * grew to 1.4-1.6 while every plain `<Text>` beside it stopped at the ceiling.
 * Two elements in one row, growing at different rates, is exactly the drifting
 * spacing that was reported.
 *
 * Passing nothing lets the patched default apply, which is the point: ONE
 * ceiling, no table to keep in sync, and no variant that can quietly opt itself
 * out. A call site that genuinely needs its own bound still passes
 * `maxFontSizeMultiplier` explicitly and still wins.
 */

/**
 * The app-wide ceiling on OS text scaling, in ONE place.
 *
 * The bound itself is enforced natively — patches/react-native+0.85.3.patch
 * clamps `maxFontSizeMultiplier` inside React Native's `Text` and `TextInput`,
 * so it cannot be missed by a call site. This constant is the JavaScript half:
 * code that needs to reason about the effective scale (see the remount key in
 * app/_layout) has to agree with the patch exactly, or it reasons about a
 * number the renderer is not using.
 *
 * tests/native-deps/font-scale-patch.test.ts asserts the two are equal, so
 * changing one without the other fails rather than drifts.
 */
export const MAX_FONT_SCALE = 1.2;

export function ThemedText({
  variant = "body",
  tone = "default",
  center,
  style,
  maxFontSizeMultiplier,
  ...rest
}: ThemedTextProps) {
  const c = useTheme();
  const color =
    tone === "secondary"
      ? c.textSecondary
      : tone === "muted"
        ? c.textMuted
        : tone === "tint"
          ? c.tint
          : tone === "onTint"
            ? c.onTint
            : tone === "danger"
              ? c.danger
              : tone === "success"
                ? c.success
                : c.text;

  return (
    <Text
      maxFontSizeMultiplier={maxFontSizeMultiplier}
      style={[styles[variant], { color }, center && styles.center, style]}
      {...rest}
    />
  );
}

const styles = StyleSheet.create({
  display: { fontSize: FontSize.xxxl, fontWeight: "800", letterSpacing: -0.5 },
  title: { fontSize: FontSize.xxl, fontWeight: "800", letterSpacing: -0.4 },
  subtitle: { fontSize: FontSize.lg, fontWeight: "700" },
  body: { fontSize: FontSize.md, fontWeight: "400", lineHeight: 23 },
  bodyStrong: { fontSize: FontSize.md, fontWeight: "600", lineHeight: 23 },
  label: { fontSize: FontSize.sm, fontWeight: "600", letterSpacing: 0.2 },
  caption: { fontSize: FontSize.xs, fontWeight: "500" },
  link: { fontSize: FontSize.md, fontWeight: "600" },
  center: { textAlign: "center" },
});
